import Uploading from "components/Uploading";
import { WISDOM_PMS_ROLE_PERMISSION } from "constants/constants";
import React, { useEffect, useMemo, useState } from "react";
import { fetchWisdomWCPDashboard } from "services/wisdom/wisdomData";
import { roundNumber } from "utils/roundNumber";

const WEAK_THRESHOLD = 70;
const SOURCE_COLORS = ["#2E93CC", "#E0A32B", "#8B7CE8", "#E0704F", "#22B07D", "#D8543F", "#5FE39D", "#B09509"];
const BANDS = [
    { lb: "ممتاز ٩٠٪+", c: "#22B07D", t: 90 },
    { lb: "جيد جدًا ٨٠–٨٩٪", c: "#2E93CC", t: 80 },
    { lb: "جيد ٧٠–٧٩٪", c: "#E0A32B", t: 70 },
    { lb: "يحتاج تحسين", c: "#D8543F", t: 0 },
];

const scoreColor = (v) => v >= 90 ? "#22B07D" : v >= 80 ? "#2E93CC" : v >= 70 ? "#E0A32B" : "#D8543F";
const fmt = (v) => v == null ? "—" : `${roundNumber(v)}%`;
const avg = (bucket) => (bucket && bucket.count) ? bucket.sum / bucket.count : null;

const employeeFullName = (employee) =>
    [employee?.first_name, employee?.middle_name, employee?.last_name].filter(Boolean).join(" ") || "بدون اسم";

const WCPDashboard = ({ orgId, year, month }) => {
    const [fetching, setFetching] = useState(true);
    const [dashboard, setDashboard] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchWisdomWCPDashboard(orgId, year, month);
                setDashboard(response || []);
                setSelectedDomain(null);
                setSelectedSource(null);
            } catch (err) {
                console.error("error:", err);
            } finally {
                setFetching(false);
            }
        };

        load();
    }, [orgId, year, month]);

    const data = useMemo(() => {
        const sources = {};
        const domains = {};
        const bySourceCount = {};
        const bySourceReport = {};
        const byDomainAll = {};
        const bySourceDomain = {};
        const bySourceForms = {};
        const domainSourcesSeen = {};
        let processedCount = 0;
        let scoreSum = 0;

        dashboard.forEach((report) => {
            const results = report.results || [];
            if (!results.length) return;

            const form = results[0]?.questionResult?.sub_field?.field?.form;
            const formType = (form?.code || "").split(" | ")[1];
            if (formType && formType !== "WCP") return;
            const sourceKey = (form?.code || "").split(" | ")[0] || "?";

            if (!sources[sourceKey]) {
                sources[sourceKey] = {
                    key: sourceKey,
                    label: WISDOM_PMS_ROLE_PERMISSION[sourceKey] || sourceKey,
                    color: SOURCE_COLORS[Object.keys(sources).length % SOURCE_COLORS.length],
                };
            }

            processedCount += 1;
            scoreSum += Number(report.avg_score) || 0;

            bySourceCount[sourceKey] = (bySourceCount[sourceKey] || 0) + 1;
            bySourceReport[sourceKey] ??= { sum: 0, count: 0 };
            bySourceReport[sourceKey].sum += Number(report.avg_score) || 0;
            bySourceReport[sourceKey].count += 1;

            bySourceForms[sourceKey] ??= [];
            bySourceForms[sourceKey].push({
                id: report.id ?? `${sourceKey}-${bySourceForms[sourceKey].length}`,
                name: employeeFullName(report.employee?.employee),
                score: Number(report.avg_score) || 0,
                date: report.createdAt || report.created_at || null,
            });

            const domainSums = {};
            results.forEach((r) => {
                const field = r.questionResult?.sub_field?.field;
                if (!field || r.score == null || !r.questionResult?.max_score) return;

                const domainKey = field.ar_name || field.en_name || `field-${field.id}`;
                domains[domainKey] ??= { key: domainKey, label: domainKey };
                domainSums[domainKey] ??= { sum: 0, count: 0 };
                domainSums[domainKey].sum += (Number(r.score) / Number(r.questionResult.max_score)) * 100;
                domainSums[domainKey].count += 1;
            });

            Object.entries(domainSums).forEach(([domainKey, bucket]) => {
                const domainScore = bucket.sum / bucket.count;

                bySourceDomain[sourceKey] ??= {};
                bySourceDomain[sourceKey][domainKey] ??= { sum: 0, count: 0 };
                bySourceDomain[sourceKey][domainKey].sum += domainScore;
                bySourceDomain[sourceKey][domainKey].count += 1;

                byDomainAll[domainKey] ??= { sum: 0, count: 0 };
                byDomainAll[domainKey].sum += domainScore;
                byDomainAll[domainKey].count += 1;

                domainSourcesSeen[domainKey] ??= new Set();
                domainSourcesSeen[domainKey].add(sourceKey);
            });
        });

        const sourceList = Object.values(sources);

        const domainAvg = {};
        Object.keys(domains).forEach((k) => { domainAvg[k] = avg(byDomainAll[k]); });

        const domainList = Object.values(domains).sort(
            (a, b) => (domainAvg[a.key] ?? 100) - (domainAvg[b.key] ?? 100)
        );

        const sourceAvg = {};
        sourceList.forEach((s) => { sourceAvg[s.key] = avg(bySourceReport[s.key]); });

        const overall = processedCount ? scoreSum / processedCount : null;

        const weakDomains = domainList.filter((d) => domainAvg[d.key] != null && domainAvg[d.key] < WEAK_THRESHOLD);

        let totalFlags = 0;
        sourceList.forEach((s) => {
            domainList.forEach((d) => {
                const v = avg(bySourceDomain[s.key]?.[d.key]);
                if (v != null && v < WEAK_THRESHOLD) totalFlags++;
            });
        });

        const uncoveredDomains = domainList.filter((d) => {
            const seen = domainSourcesSeen[d.key] || new Set();
            return [...seen].filter((k) => k !== "Self").length === 0;
        });

        return {
            sources: sourceList,
            domains: domainList,
            bySourceCount,
            sourceAvg,
            domainAvg,
            bySourceDomain,
            bySourceForms,
            totalReports: processedCount,
            overall,
            totalFlags,
            weakDomains,
            uncoveredDomains,
            worstDomain: domainList[0] || null,
        };
    }, [dashboard]);

    if (fetching) return <Uploading />;

    if (!data.totalReports) {
        return (
            <div dir="rtl" className="w-[min(92vw,700px)] p-8 text-center text-[#9DB2CE]">
                لا توجد بيانات تقييم متاحة لهذه الجهة بعد.
            </div>
        );
    }

    const bandCounts = BANDS.map((b, i) => data.domains.filter((d) => {
        const v = data.domainAvg[d.key];
        return v != null && v >= b.t && (i === 0 || v < BANDS[i - 1].t);
    }).length);

    let ringOffset = 0;

    const selected = selectedDomain ? data.domains.find((d) => d.key === selectedDomain) : null;
    let detailPoints = [];
    let detailLo = 0;
    let detailHi = 100;
    let detailWeak = [];
    if (selected) {
        detailPoints = data.sources
            .map((s) => ({ source: s, value: avg(data.bySourceDomain[s.key]?.[selected.key]) }))
            .filter((p) => p.value != null);
        const values = detailPoints.map((p) => p.value);
        if (values.length) {
            detailLo = Math.max(0, Math.min(...values) - 5);
            detailHi = Math.min(100, Math.max(...values) + 5);
        }
        detailWeak = detailPoints.filter((p) => p.value < WEAK_THRESHOLD);
    }

    const selectedSourceObj = selectedSource ? data.sources.find((s) => s.key === selectedSource) : null;
    const selectedForms = selectedSourceObj
        ? (data.bySourceForms[selectedSourceObj.key] || []).slice().sort((a, b) => b.score - a.score)
        : [];

    return (
        <div dir="rtl" className="w-[min(94vw,1080px)] text-[#EAF1FA]">
            <div className="mb-1">
                <h1 className="text-lg font-bold">تحليل المشاركة المجتمعية</h1>
                <p className="text-xs text-[#6C82A3] mt-0.5">
                    {data.sources.length} مصادر تقييم · {data.domains.length} مجالات · {data.totalReports} تقييم
                </p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3">
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">النتيجة العامة</span>
                    <b className="block text-xl font-semibold mt-1" style={{ color: scoreColor(data.overall ?? 0) }}>{fmt(data.overall)}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">التقييمات</span>
                    <b className="block text-xl font-semibold mt-1">{data.totalReports}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">مجالات دون {WEAK_THRESHOLD}%</span>
                    <b className="block text-xl font-semibold mt-1" style={{ color: "#D8543F" }}>{data.weakDomains.length}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">أضعف مجال</span>
                    {data.worstDomain ? (
                        <b
                            className="block text-[11px] font-semibold mt-1.5 leading-snug truncate"
                            style={{ color: scoreColor(data.domainAvg[data.worstDomain.key]) }}
                            title={data.worstDomain.label}
                        >
                            {data.worstDomain.label} · {fmt(data.domainAvg[data.worstDomain.key])}
                        </b>
                    ) : (
                        <b className="block text-xl font-semibold mt-1">—</b>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Donut: domains by performance band */}
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">مستوى المجالات</h2>
                    <div className="flex flex-col items-center gap-3">
                        <svg width="112" height="112" viewBox="0 0 112 112">
                            <g transform="translate(56,56) rotate(-90)">
                                {BANDS.map((b, i) => {
                                    if (!bandCounts[i]) return null;
                                    const C = 2 * Math.PI * 42;
                                    const len = (bandCounts[i] / data.domains.length) * C;
                                    const el = (
                                        <circle
                                            key={b.lb}
                                            r="42"
                                            fill="none"
                                            stroke={b.c}
                                            strokeWidth="15"
                                            strokeDasharray={`${Math.max(len - 2, 0)} ${C - len + 2}`}
                                            strokeDashoffset={-ringOffset}
                                        />
                                    );
                                    ringOffset += len;
                                    return el;
                                })}
                            </g>
                            <text x="56" y="54" textAnchor="middle" fill="#EAF1FA" fontSize="18" fontWeight="600">
                                {data.domains.length}
                            </text>
                            <text x="56" y="67" textAnchor="middle" fill="#6C82A3" fontSize="8">مجالات</text>
                        </svg>
                        <div className="w-full flex flex-col gap-1">
                            {BANDS.map((b, i) => bandCounts[i] ? (
                                <div key={b.lb} className="grid grid-cols-[8px_1fr_auto] items-center gap-2 text-[11px]">
                                    <span className="w-2 h-2 rounded-sm" style={{ background: b.c }} />
                                    <span className="text-[#9DB2CE] truncate">{b.lb}</span>
                                    <span className="font-semibold">{bandCounts[i]}</span>
                                </div>
                            ) : null)}
                        </div>
                    </div>
                </section>

                {/* Source averages */}
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                        متوسط النتائج حسب المصدر <em className="text-[10px] text-[#6C82A3] font-normal">وعدد التقييمات — اضغط لعرض النماذج</em>
                    </h2>
                    <div className="flex flex-col gap-2">
                        {data.sources.slice().sort((a, b) => (data.sourceAvg[b.key] ?? 0) - (data.sourceAvg[a.key] ?? 0)).map((s) => {
                            const isSel = selectedSource === s.key;
                            return (
                                <div
                                    key={s.key}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedSource(isSel ? null : s.key)}
                                    className={`cursor-pointer rounded-md p-1 -m-1 border ${isSel ? "bg-[#26385A] border-[#33507E]" : "border-transparent hover:bg-[#26385A]/60"}`}
                                >
                                    <div className="flex items-baseline gap-1 text-[11px] mb-1">
                                        <b className="font-medium">{s.label}</b>
                                        <span className="text-[#6C82A3] text-[10px]">{data.bySourceCount[s.key] || 0} تقييم</span>
                                        <span className="mr-auto font-semibold" style={{ color: s.color }}>{fmt(data.sourceAvg[s.key])}</span>
                                    </div>
                                    <div className="h-[9px] rounded-sm bg-[#26385A] overflow-hidden">
                                        <div
                                            className="h-full rounded-sm"
                                            style={{ width: `${data.sourceAvg[s.key] || 0}%`, background: s.color }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>

            {/* Domain x Source matrix */}
            <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                    المجالات حسب المصدر <em className="text-[10px] text-[#6C82A3] font-normal">اضغط على أي مجال لعرض مساره حسب المصدر</em>
                </h2>

                {data.worstDomain && (
                    <div className="rounded-lg border border-[#D8543F]/40 bg-[#D8543F]/10 text-[#F0B4AA] text-[11px] leading-relaxed p-2 mb-2">
                        <b className="block text-[#FFD3CA] font-semibold mb-0.5">
                            أضعف مجال: {data.worstDomain.label} بمتوسط {fmt(data.domainAvg[data.worstDomain.key])}
                        </b>
                        تم رصد {data.totalFlags} نقطة ضعف (أقل من {WEAK_THRESHOLD}%) عبر المصادر والمجالات.
                    </div>
                )}

                {data.uncoveredDomains.length > 0 && (
                    <div className="rounded-lg border border-[#E0A32B]/40 bg-[#E0A32B]/10 text-[#EFCE94] text-[11px] leading-relaxed p-2 mb-2">
                        <b className="block text-[#FBE6BC] font-semibold mb-0.5">
                            {data.uncoveredDomains.length} مجال بلا مقيّم خارجي
                        </b>
                        {data.uncoveredDomains.map((d) => d.label).join("، ")} — لا يقيّمه سوى المصدر الذاتي.
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
                        <thead>
                            <tr>
                                <th className="text-[9px] font-medium text-[#6C82A3] text-right w-32">المجال</th>
                                {data.sources.map((s) => (
                                    <th key={s.key} className="text-[9px] font-medium text-[#9DB2CE] w-12">{s.label}</th>
                                ))}
                                <th className="text-[9px] font-medium text-[#9DB2CE] w-12">المتوسط</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.domains.map((d) => {
                                const isSel = selectedDomain === d.key;
                                return (
                                    <tr
                                        key={d.key}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => setSelectedDomain(isSel ? null : d.key)}
                                        className="cursor-pointer"
                                    >
                                        <td
                                            className="text-[10px] text-right pe-1 truncate max-w-[130px]"
                                            style={{ color: isSel ? "#8FCBEC" : undefined }}
                                        >
                                            {d.label}
                                        </td>
                                        {data.sources.map((s) => {
                                            const v = avg(data.bySourceDomain[s.key]?.[d.key]);
                                            return (
                                                <td key={s.key}>
                                                    {v == null ? (
                                                        <div className="h-7 rounded-md flex items-center justify-center text-[#4E627E] bg-white/[0.02]">—</div>
                                                    ) : (
                                                        <div
                                                            className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold"
                                                            style={{
                                                                background: `${scoreColor(v)}22`,
                                                                color: scoreColor(v),
                                                                boxShadow: v < WEAK_THRESHOLD ? `inset 0 0 0 1.5px ${scoreColor(v)}` : "none",
                                                                outline: isSel ? "1px solid #33507E" : "none",
                                                            }}
                                                        >
                                                            {roundNumber(v)}%
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td>
                                            <div
                                                className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold bg-[#26385A] border border-[#33507E]"
                                                style={{ color: scoreColor(data.domainAvg[d.key]), outline: isSel ? "1px solid #33507E" : "none" }}
                                            >
                                                {fmt(data.domainAvg[d.key])}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-wrap gap-3 text-[10px] text-[#9DB2CE] mt-2">
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#22B07D" }} />٩٠+</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#2E93CC" }} />٨٠–٨٩</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#E0A32B" }} />٧٠–٧٩</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#D8543F" }} />أقل من {WEAK_THRESHOLD} · نقطة ضعف</span>
                    <span className="text-[#6C82A3]">— = المجال غير مشمول لهذا المصدر</span>
                </div>

                {!selected ? (
                    <p className="text-[9.5px] text-[#6C82A3] text-center pt-3 pb-0.5">اضغط على أي مجال لعرض مساره حسب المصدر</p>
                ) : (
                    <div className="rounded-lg border border-[#33507E] bg-[#16233A] p-3 mt-3">
                        <div className="text-[13px] font-semibold mb-2">
                            {selected.label} · <span style={{ color: scoreColor(data.domainAvg[selected.key]) }}>{fmt(data.domainAvg[selected.key])}</span>
                        </div>

                        {detailPoints.length > 0 ? (
                            <>
                                <div className="flex items-end gap-1.5" style={{ height: 96 }}>
                                    {detailPoints.map((p) => (
                                        <div key={p.source.key} className="flex-1 flex flex-col justify-end items-center gap-1 h-full">
                                            <span className="text-[9.5px] font-semibold" style={{ color: scoreColor(p.value) }}>{roundNumber(p.value)}%</span>
                                            <span
                                                className="w-full rounded-t"
                                                style={{
                                                    height: `${Math.round(((p.value - detailLo) / ((detailHi - detailLo) || 1)) * 64) + 8}px`,
                                                    background: scoreColor(p.value),
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div className="flex gap-1.5 mt-1">
                                    {detailPoints.map((p) => (
                                        <span key={p.source.key} className="flex-1 text-center text-[8.5px] text-[#6C82A3] truncate">{p.source.label}</span>
                                    ))}
                                </div>
                                <div className="text-[8.5px] text-[#6C82A3] text-center mt-1.5">
                                    المحور بين {roundNumber(detailLo)}% و{roundNumber(detailHi)}% لإبراز الفروق
                                </div>
                            </>
                        ) : (
                            <p className="text-[10.5px] text-[#6C82A3] text-center py-2">لا توجد تقييمات كافية لهذا المجال.</p>
                        )}

                        {detailPoints.length > 0 && (
                            detailWeak.length ? (
                                <div className="rounded-md border border-[#D8543F]/30 bg-[#D8543F]/10 text-[#F0B4AA] text-[10px] leading-relaxed p-2 mt-2">
                                    <b className="block text-[#FFD3CA] text-[11px] mb-1">نقطة ضعف قائمة</b>
                                    المتوسط {fmt(data.domainAvg[selected.key])} أقل من الحد {WEAK_THRESHOLD}%، حسب: {detailWeak.map((p) => `${p.source.label} ${roundNumber(p.value)}%`).join("، ")}.
                                </div>
                            ) : (
                                <div className="rounded-md border border-[#22B07D]/30 bg-[#22B07D]/10 text-[#9FE0C6] text-[10px] leading-relaxed p-2 mt-2">
                                    <b className="block text-[#C6F0DF] text-[11px] mb-1">فوق حد {WEAK_THRESHOLD}%</b>
                                    كل المصادر التي قيّمت هذا المجال ضمن الحد المقبول أو أعلى.
                                </div>
                            )
                        )}
                    </div>
                )}
            </section>

            {/* Forms for the selected source */}
            {selectedSourceObj && (
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 rounded-sm" style={{ background: selectedSourceObj.color }} />
                        <h2 className="text-[13px] font-semibold text-[#9DB2CE]">
                            نماذج {selectedSourceObj.label}
                            <em className="text-[10px] text-[#6C82A3] font-normal ms-1">({selectedForms.length} نموذج)</em>
                        </h2>
                        <button
                            type="button"
                            onClick={() => setSelectedSource(null)}
                            className="mr-auto text-[10px] text-[#6C82A3] hover:text-[#EAF1FA]"
                        >
                            إغلاق ✕
                        </button>
                    </div>

                    <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto pe-1">
                        {selectedForms.map((f) => {
                            const d = f.date ? new Date(f.date) : null;
                            const dateLabel = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("ar-EG") : "—";
                            return (
                                <div
                                    key={f.id}
                                    className="grid grid-cols-[1fr_auto_56px] items-center gap-2 rounded-md border border-[#26385A] bg-[#16233A] px-2 py-1.5"
                                >
                                    <span className="text-[11px] text-[#EAF1FA] truncate">{f.name}</span>
                                    <span className="text-[10px] text-[#6C82A3]">{dateLabel}</span>
                                    <span
                                        className="text-[11px] font-semibold text-center rounded-md py-0.5"
                                        style={{ background: `${scoreColor(f.score)}22`, color: scoreColor(f.score) }}
                                    >
                                        {roundNumber(f.score)}%
                                    </span>
                                </div>
                            );
                        })}
                        {!selectedForms.length && (
                            <p className="text-[11px] text-[#6C82A3] text-center py-4">لا توجد نماذج لهذا المصدر.</p>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default WCPDashboard;
